import { StyledTextField, styledTextFieldClasses, ActionPanelContainer } from "./HyroTextField.styles";
import React, { useEffect, useState } from "react";
import FieldCopy from "src/components/widgets/FieldCopyPaste/FieldCopy";
import { useTheme } from "@mui/material";
import { LAYOUTS, TYPES } from "src/components/widgets/FieldCopyPaste/CopyPaste.consts";
import { StyledIconButton } from "src/components/widgets/FieldCopyPaste/CopyPaste.commons";
import Tooltip from "@hyro-components/Tooltip";
import CodeExpressionIcon from "src/icons/field-actions/CodeExpressionIcon";
import TTSPlayer from "src/components/widgets/tts-player/TTSPlayer";
import PlayPauseButton from "src/components/widgets/tts-player/PlayPauseButton";
import { useSpeechPlayer } from "src/utils/core/hooks";
import FieldActionMenu from "./FieldActionMenu";

const HyroTextField = ({ ...props }) => {
  const theme = useTheme();
  const {
    InputProps,
    value,
    isFieldReadOnlyFromSpec,
    showCopyButton = true,
    showPlayerButton = false,
    showExpressionButton = false,
    onExpressionClick,
    onRevertToDefault,
    usesExpression = false
  } = props;
  const extraActionsCount = [showPlayerButton, showExpressionButton].filter(Boolean).length;
  const hasMultipleExtraActions = extraActionsCount > 1;
  const showActionPanel = showCopyButton && extraActionsCount > 0 && value;
  const onlyCopyButtonVisible = showCopyButton && value && extraActionsCount === 0;

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const {
    isOpen,
    isPlaying,
    isLoading,
    handlePlayPause,
    closeSpeechPlayer
  } = useSpeechPlayer({ instanceId: props.fieldKey });

  useEffect(() => () => {
    closeSpeechPlayer();
  }, [closeSpeechPlayer]);

  const renderSingleExtraAction = () => {
    if (showPlayerButton) {
      return (
        <PlayPauseButton
          onClick={() => !usesExpression && handlePlayPause({ text: value })}
          disabled={usesExpression}
          isPlaying={isPlaying}
          isLoading={isLoading}
        />
      );
    }
    if (showExpressionButton) {
      return (
        <Tooltip disableInteractive title="Custom expression">
          <StyledIconButton
            onClick={onExpressionClick}
            size={26}
            type={TYPES.FLAT}
            layout={LAYOUTS.ROUNDED}
            data-testid="custom-expression-button"
          >
            <CodeExpressionIcon size={18} style={{ flexShrink: 0 }} />
          </StyledIconButton>
        </Tooltip>
      );
    }
    return null;
  };

  const handleRender = () => {
    if (showActionPanel) {
      return (
        <ActionPanelContainer>
          <FieldCopy
            type={isFieldReadOnlyFromSpec && !onRevertToDefault ? "elevated" : "flat"}
            zIndex={theme.zIndex.drawer + 1}
            value={value}
            iconSize={18}
            buttonSize={26}
            layout={LAYOUTS.ROUNDED}
          />
          {hasMultipleExtraActions ? (
            <FieldActionMenu
              showPlayerButton={showPlayerButton}
              showExpressionButton={showExpressionButton}
              isPlaying={isPlaying}
              isLoading={isLoading}
              onPlayPause={() => handlePlayPause({ text: value })}
              onExpressionClick={onExpressionClick}
              onRevertToDefault={onRevertToDefault}
              onOpenChange={setIsMenuOpen}
              isPlayDisabled={usesExpression}
            />
          ) : renderSingleExtraAction()}
        </ActionPanelContainer>
      );
    }
    if (onlyCopyButtonVisible) {
      return (
        <FieldCopy
          type={isFieldReadOnlyFromSpec ? "elevated" : "flat"}
          zIndex={theme.zIndex.drawer + 1}
          value={value}
          iconSize={16}
          buttonSize={32}
          layout={LAYOUTS.CIRCLE}
          useWrapper
        />
      );
    }
    return null;
  };

  return (
    <>
      <StyledTextField
        {...props}
        allowTextCursor={true}
        forceActionPanel={isOpen || isMenuOpen}
        slotProps={{
          input: {
            ...InputProps,
            endAdornment: (
              <>
                {InputProps?.endAdornment}
                <span className={styledTextFieldClasses.copyIcon}>
                  {handleRender()}
                </span>
              </>
            )
          }
        }}
      />
      {isOpen && showPlayerButton && <TTSPlayer instanceId={props.fieldKey} />}
    </>
  );
};

export default HyroTextField;
